import { EditorState, SerializedEditorState, SerializedLexicalNode } from 'lexical';
import type { SerializedListItemNode, SerializedListNode } from '@lexical/list';
import {
  FlexibleBlockNode,
  FlexibleInlineLinebreakNode,
  FlexibleInlineLinkNode,
  FlexibleInlineNode,
  FlexibleInlineTextNode,
  FlexibleInlineVariableNode,
  FlexibleListItemNode,
  FlexibleListNode,
  FlexibleParagraphNode,
  FlexibleSimpleInlineNode,
  FlexibleTextContent,
} from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import type { RichText, RichTextNode } from '@mutiny-pkg/champaign-schemas/src/schemas/richText/interfaces';
import type { StreamingRichText } from '@mutiny-pkg/champaign-schemas/src/schemas/richText/streaming';
import type { ThemeProperties } from '@mutiny-pkg/champaign-schemas/src/schemas/themeProperties/types';
import type { MentionItem } from './MentionsPlugin/MentionsPlugin';
import { toEditorState, normalizeStreamingRichText } from './ast';
import { renderSerializedEditorStateToHtml } from './ssr';
import type { SerializedCustomParagraphNode } from './CustomParagraphNode/CustomParagraphNode';
import type { SerializedExtendedTextNode } from './ExtendedTextPlugin/ExtendedTextNode';
import type { SerializedMentionNode } from './MentionsPlugin/MentionNode';
import type { SerializedCustomLinkNode } from './CustomLinkNode/CustomLinkNode';

const BOLD_MASK = 1;
const ITALIC_MASK = 2;
const STRIKETHROUGH_MASK = 4;
const UNDERLINE_MASK = 8;

type RenderOptions = {
  isEditor: boolean;
  mentionItems: MentionItem[];
};

// Type guards for nested-discriminant narrowing (TypeScript doesn't narrow on `node.data.type` checks alone)
function isLinkInline(node: FlexibleInlineNode): node is FlexibleInlineLinkNode {
  return node.data.type === 'link';
}

function isParagraphBlock(node: FlexibleBlockNode): node is FlexibleParagraphNode {
  return node.data.type === 'paragraph';
}

function isListBlock(node: FlexibleBlockNode): node is FlexibleListNode {
  return node.data.type === 'list';
}

function flexibleInlineNodeToLexical(node: FlexibleInlineNode): SerializedLexicalNode {
  if (node.data.type === 'text') {
    const format =
      (node.data.bold ? BOLD_MASK : 0) |
      (node.data.italic ? ITALIC_MASK : 0) |
      (node.data.strikethrough ? STRIKETHROUGH_MASK : 0) |
      (node.data.underline ? UNDERLINE_MASK : 0);
    const serialized: SerializedExtendedTextNode = {
      type: 'extended-text',
      mode: 'normal' as const,
      format,
      text: node.data.text,
      highlightColor: node.data.highlight,
      fontColor: node.data.color,
      resolvedHighlightColor: node.data.highlight,
      resolvedFontColor: node.data.color,
      detail: 0,
      style: '',
      version: 1,
    };
    return serialized;
  }

  if (node.data.type === 'variable') {
    const serialized: SerializedMentionNode = {
      type: 'mention',
      value: node.data.columnId,
      version: 1,
      format:
        (node.data.bold ? BOLD_MASK : 0) |
        (node.data.italic ? ITALIC_MASK : 0) |
        (node.data.strikethrough ? STRIKETHROUGH_MASK : 0) |
        (node.data.underline ? UNDERLINE_MASK : 0),
      highlightColor: node.data.highlight,
      fontColor: node.data.color,
      style: '',
      mode: 'normal',
      detail: 0,
      text: '',
    };
    return serialized;
  }

  if (node.data.type === 'linebreak') {
    return { type: 'linebreak', version: 1 };
  }

  if (isLinkInline(node)) {
    const serialized: SerializedCustomLinkNode = {
      type: 'custom-link',
      url: node.data.href,
      children: node.children.map(flexibleInlineNodeToLexical),
      direction: null,
      format: '',
      indent: 0,
      version: 1,
    };
    return serialized;
  }

  throw new Error('Unknown flexible inline node type');
}

function flexibleBlockNodeToLexical(node: FlexibleBlockNode): SerializedLexicalNode {
  if (isParagraphBlock(node)) {
    const para: SerializedCustomParagraphNode = {
      type: 'custom-paragraph',
      textFormat: 0,
      textStyle: '',
      children: node.children.map(flexibleInlineNodeToLexical),
      format: node.data.format ?? '',
      indent: 0,
      direction: null,
      version: 1,
    };
    return para;
  }

  if (isListBlock(node)) {
    const listChildren: SerializedListItemNode[] = node.children.map((item, index) => ({
      type: 'listitem',
      value: index + 1,
      children: item.children.map(flexibleInlineNodeToLexical),
      indent: 0,
      version: 1,
      checked: undefined,
      direction: null,
      format: '',
    }));
    const listNode: SerializedListNode = {
      type: 'list',
      listType: node.data.listType === 'ordered' ? 'number' : 'bullet',
      start: 1,
      tag: node.data.listType === 'ordered' ? 'ol' : 'ul',
      children: listChildren,
      indent: 0,
      version: 1,
      direction: null,
      format: '',
    };
    return listNode;
  }

  throw new Error('Unknown flexible block node type');
}

export function flexibleTextToEditorState(content: FlexibleTextContent | RichText | string): SerializedEditorState {
  if (typeof content === 'string') {
    return toEditorState(content);
  }

  if (!Array.isArray(content)) {
    return toEditorState(content as RichText);
  }

  const blocks = content as FlexibleBlockNode[];
  let children: SerializedLexicalNode[];
  if (blocks.length === 0) {
    const emptyBlock: FlexibleBlockNode = {
      data: { type: 'paragraph' },
      children: [{ data: { type: 'text', text: '' } }],
    };
    children = [flexibleBlockNodeToLexical(emptyBlock)];
  } else {
    children = blocks.map(flexibleBlockNodeToLexical);
  }

  return {
    root: {
      children,
      direction: 'ltr',
      format: '',
      indent: 0,
      type: 'root',
      version: 1,
    },
  };
}

function lexicalNodeToFlexibleInlineText(node: SerializedExtendedTextNode): FlexibleInlineTextNode {
  const format = typeof node.format === 'number' ? node.format : 0;
  const data: FlexibleInlineTextNode['data'] = {
    type: 'text',
    text: node.text ?? '',
  };
  if (format & BOLD_MASK) data.bold = true;
  if (format & ITALIC_MASK) data.italic = true;
  if (format & STRIKETHROUGH_MASK) data.strikethrough = true;
  if (format & UNDERLINE_MASK) data.underline = true;
  if (node.fontColor) data.color = node.fontColor;
  if (node.highlightColor) data.highlight = node.highlightColor;
  return { data };
}

function lexicalNodeToFlexibleInline(node: SerializedLexicalNode): FlexibleInlineNode | null {
  // Check mention before extended-text since MentionNode extends ExtendedTextNode
  if (node.type === 'mention') {
    const mentionNode = node as SerializedMentionNode;
    const format = typeof mentionNode.format === 'number' ? mentionNode.format : 0;
    const data: FlexibleInlineVariableNode['data'] = { type: 'variable', columnId: mentionNode.value };
    if (format & BOLD_MASK) data.bold = true;
    if (format & ITALIC_MASK) data.italic = true;
    if (format & STRIKETHROUGH_MASK) data.strikethrough = true;
    if (format & UNDERLINE_MASK) data.underline = true;
    if (mentionNode.fontColor) data.color = mentionNode.fontColor;
    if (mentionNode.highlightColor) data.highlight = mentionNode.highlightColor;
    return { data };
  }

  if (node.type === 'linebreak') {
    return { data: { type: 'linebreak' } } as FlexibleInlineLinebreakNode;
  }

  if (node.type === 'custom-link' || node.type === 'link' || node.type === 'autolink') {
    const linkNode = node as SerializedCustomLinkNode;
    const children = (linkNode.children ?? [])
      .map((child) => {
        const inline = lexicalNodeToFlexibleInline(child);
        if (inline === null || inline.data.type === 'link') return null;
        return inline as FlexibleSimpleInlineNode;
      })
      .filter((n): n is FlexibleSimpleInlineNode => n !== null);
    return { data: { type: 'link', href: linkNode.url ?? '' }, children } as FlexibleInlineLinkNode;
  }

  if (node.type === 'extended-text' || node.type === 'text') {
    return lexicalNodeToFlexibleInlineText(node as SerializedExtendedTextNode);
  }

  return null;
}

function lexicalNodeToFlexibleBlock(node: SerializedLexicalNode): FlexibleBlockNode | null {
  if (node.type === 'paragraph' || node.type === 'custom-paragraph') {
    const paraNode = node as { children?: SerializedLexicalNode[]; format?: string };
    const children = (paraNode.children ?? [])
      .map(lexicalNodeToFlexibleInline)
      .filter((n): n is FlexibleInlineNode => n !== null);
    const data: FlexibleParagraphNode['data'] = { type: 'paragraph' };
    const fmt = paraNode.format;
    if (fmt && fmt !== '') {
      data.format = fmt as FlexibleParagraphNode['data']['format'];
    }
    const result: FlexibleParagraphNode = { data, children };
    return result;
  }

  if (node.type === 'list') {
    const listNode = node as SerializedListNode;
    const children = (listNode.children ?? [])
      .filter((child): child is SerializedListItemNode => child.type === 'listitem')
      .map((item): FlexibleListItemNode => {
        const itemChildren = (item.children ?? [])
          .map(lexicalNodeToFlexibleInline)
          .filter((n): n is FlexibleInlineNode => n !== null);
        const result: FlexibleListItemNode = { data: { type: 'listitem' }, children: itemChildren };
        return result;
      });
    const result: FlexibleListNode = {
      data: {
        type: 'list',
        listType: listNode.listType === 'number' ? 'ordered' : 'unordered',
      },
      children,
    };
    return result;
  }

  return null;
}

export function editorStateToFlexibleText(editorState: EditorState): FlexibleTextContent {
  const { root } = editorState.toJSON();
  return root.children.map(lexicalNodeToFlexibleBlock).filter((n): n is FlexibleBlockNode => n !== null);
}

function normalizeFlexibleInlineNode(node: unknown): FlexibleInlineNode {
  if (!node || typeof node !== 'object') {
    return { data: { type: 'text', text: '' } };
  }

  const n = node as { data?: unknown; children?: unknown };

  if (!n.data || typeof n.data !== 'object') {
    return { data: { type: 'text', text: '' } };
  }

  const dataObj = n.data as { type?: string };

  if (!dataObj.type || typeof dataObj.type !== 'string') {
    return { data: { type: 'text', text: '' } };
  }

  if (dataObj.type === 'text') {
    const d = dataObj as Partial<FlexibleInlineTextNode['data']>;
    const data: FlexibleInlineTextNode['data'] = {
      type: 'text',
      text: typeof d.text === 'string' ? d.text : '',
    };
    if (d.bold) data.bold = true;
    if (d.italic) data.italic = true;
    if (d.strikethrough) data.strikethrough = true;
    if (d.underline) data.underline = true;
    if (d.color) data.color = d.color;
    if (d.highlight) data.highlight = d.highlight;
    return { data };
  }

  if (dataObj.type === 'variable') {
    const d = dataObj as Partial<FlexibleInlineVariableNode['data']>;
    const data: FlexibleInlineVariableNode['data'] = {
      type: 'variable',
      columnId: typeof d.columnId === 'string' ? d.columnId : '',
    };
    if (d.bold) data.bold = true;
    if (d.italic) data.italic = true;
    if (d.strikethrough) data.strikethrough = true;
    if (d.underline) data.underline = true;
    if (d.color) data.color = d.color;
    if (d.highlight) data.highlight = d.highlight;
    return { data };
  }

  if (dataObj.type === 'linebreak') {
    return { data: { type: 'linebreak' } };
  }

  if (dataObj.type === 'link') {
    const d = dataObj as Partial<FlexibleInlineLinkNode['data']>;
    const children = Array.isArray(n.children)
      ? n.children.map(normalizeFlexibleInlineNode).filter((c): c is FlexibleSimpleInlineNode => c.data.type !== 'link')
      : [];
    return { data: { type: 'link', href: typeof d.href === 'string' ? d.href : '' }, children };
  }

  return { data: { type: 'text', text: '' } };
}

function normalizeFlexibleBlockNode(node: unknown): FlexibleBlockNode {
  if (!node || typeof node !== 'object') {
    return { data: { type: 'paragraph' }, children: [] };
  }

  const n = node as { data?: unknown; children?: unknown };

  if (!n.data || typeof n.data !== 'object') {
    return { data: { type: 'paragraph' }, children: [] };
  }

  const dataObj = n.data as { type?: string };

  if (!dataObj.type || typeof dataObj.type !== 'string') {
    return { data: { type: 'paragraph' }, children: [] };
  }

  if (dataObj.type === 'paragraph') {
    const d = dataObj as Partial<FlexibleParagraphNode['data']>;
    const children = Array.isArray(n.children) ? n.children.map(normalizeFlexibleInlineNode) : [];
    const data: FlexibleParagraphNode['data'] = { type: 'paragraph' };
    if (d.format) data.format = d.format;
    const result: FlexibleParagraphNode = { data, children };
    return result;
  }

  if (dataObj.type === 'list') {
    const d = dataObj as Partial<FlexibleListNode['data']>;
    const listType = d.listType === 'ordered' || d.listType === 'unordered' ? d.listType : 'unordered';
    const children = Array.isArray(n.children)
      ? (n.children as unknown[]).map((item): FlexibleListItemNode => {
          const itemN = item as { data?: unknown; children?: unknown };
          const itemChildren = Array.isArray(itemN?.children) ? itemN.children.map(normalizeFlexibleInlineNode) : [];
          return { data: { type: 'listitem' }, children: itemChildren };
        })
      : [];
    const result: FlexibleListNode = { data: { type: 'list', listType }, children };
    return result;
  }

  return { data: { type: 'paragraph' }, children: [] };
}

function inlineRichTextNodeToFlexible(node: RichTextNode): FlexibleInlineNode {
  if (node.type === 'text') {
    const data: FlexibleInlineTextNode['data'] = { type: 'text', text: node.content };
    if (node.decorations.bold) data.bold = true;
    if (node.decorations.italic) data.italic = true;
    if (node.decorations.strikethrough) data.strikethrough = true;
    if (node.decorations.underline) data.underline = true;
    if (node.decorations.color) data.color = node.decorations.color;
    if (node.decorations.highlight) data.highlight = node.decorations.highlight;
    return { data };
  }
  if (node.type === 'variable') {
    const data: FlexibleInlineVariableNode['data'] = { type: 'variable', columnId: node.columnId };
    if (node.decorations?.bold) data.bold = true;
    if (node.decorations?.italic) data.italic = true;
    if (node.decorations?.strikethrough) data.strikethrough = true;
    if (node.decorations?.underline) data.underline = true;
    if (node.decorations?.color) data.color = node.decorations.color;
    if (node.decorations?.highlight) data.highlight = node.decorations.highlight;
    return { data };
  }
  if (node.type === 'linebreak') {
    return { data: { type: 'linebreak' } };
  }
  if (node.type === 'link') {
    const linkChildren = node.children
      .filter((c): c is Extract<RichTextNode, { type: 'text' }> => c.type === 'text')
      .map((c): FlexibleInlineTextNode => {
        const data: FlexibleInlineTextNode['data'] = { type: 'text', text: c.content };
        if (c.decorations.bold) data.bold = true;
        if (c.decorations.italic) data.italic = true;
        if (c.decorations.strikethrough) data.strikethrough = true;
        if (c.decorations.underline) data.underline = true;
        return { data };
      });
    return { data: { type: 'link', href: node.url }, children: linkChildren };
  }
  return { data: { type: 'text', text: '' } };
}

export function richTextToFlexibleText(richText: RichText): FlexibleTextContent {
  return richText.children.map((element): FlexibleBlockNode => {
    if (element.type === 'paragraph') {
      const children: FlexibleInlineNode[] = element.children.map(inlineRichTextNodeToFlexible);
      const result: FlexibleParagraphNode = { data: { type: 'paragraph' }, children };
      return result;
    }
    if (element.type === 'list') {
      const children = element.children
        .filter((item): item is Extract<RichTextNode, { type: 'listitem' }> => item.type === 'listitem')
        .map((item): FlexibleListItemNode => {
          const itemChildren = item.children.map(inlineRichTextNodeToFlexible);
          return { data: { type: 'listitem' }, children: itemChildren };
        });
      const result: FlexibleListNode = { data: { type: 'list', listType: element.listType }, children };
      return result;
    }
    const result: FlexibleParagraphNode = { data: { type: 'paragraph' }, children: [] };
    return result;
  });
}

export function normalizeStreamingFlexibleText(value: unknown): FlexibleTextContent {
  if (value == null) {
    return [];
  }

  if (typeof value === 'string') {
    if (!value.trim()) return [];
    return [{ data: { type: 'paragraph' }, children: [{ data: { type: 'text', text: value } }] }];
  }

  if (Array.isArray(value)) {
    return value.map(normalizeFlexibleBlockNode);
  }

  if (typeof value === 'object') {
    const normalized = normalizeStreamingRichText(value as StreamingRichText);
    if (normalized === undefined || normalized === null) {
      return [];
    }
    if (typeof normalized === 'string') {
      if (!normalized.trim()) return [];
      return [{ data: { type: 'paragraph' }, children: [{ data: { type: 'text', text: normalized } }] }];
    }
    return richTextToFlexibleText(normalized);
  }

  return [];
}

function isEmptyInlineNode(node: FlexibleInlineNode): boolean {
  if (node.data.type === 'text') return !node.data.text.trim();
  if (node.data.type === 'variable') return !node.data.columnId;
  if (node.data.type === 'linebreak') return true;
  if (isLinkInline(node)) {
    return node.children.every((c) => {
      if (c.data.type === 'text') return !c.data.text.trim();
      return true;
    });
  }
  return true;
}

function isEmptyBlockNode(node: FlexibleBlockNode): boolean {
  if (isParagraphBlock(node)) return node.children.every(isEmptyInlineNode);
  if (isListBlock(node)) return node.children.every((item) => item.children.every(isEmptyInlineNode));
  return true;
}

export function isEmptyFlexibleText(content: FlexibleTextContent): boolean {
  if (content.length === 0) return true;
  return content.every(isEmptyBlockNode);
}

function isFlexibleInlineNodeEqual(a: FlexibleInlineNode, b: FlexibleInlineNode): boolean {
  if (a.data.type !== b.data.type) return false;
  if (a.data.type === 'text' && b.data.type === 'text') {
    return (
      a.data.text === b.data.text &&
      Boolean(a.data.bold) === Boolean(b.data.bold) &&
      Boolean(a.data.italic) === Boolean(b.data.italic) &&
      Boolean(a.data.strikethrough) === Boolean(b.data.strikethrough) &&
      Boolean(a.data.underline) === Boolean(b.data.underline) &&
      (a.data.color || '') === (b.data.color || '') &&
      (a.data.highlight || '') === (b.data.highlight || '')
    );
  }
  if (a.data.type === 'variable' && b.data.type === 'variable') {
    return (
      a.data.columnId === b.data.columnId &&
      Boolean(a.data.bold) === Boolean(b.data.bold) &&
      Boolean(a.data.italic) === Boolean(b.data.italic) &&
      Boolean(a.data.strikethrough) === Boolean(b.data.strikethrough) &&
      Boolean(a.data.underline) === Boolean(b.data.underline) &&
      (a.data.color || '') === (b.data.color || '') &&
      (a.data.highlight || '') === (b.data.highlight || '')
    );
  }
  if (a.data.type === 'linebreak' && b.data.type === 'linebreak') return true;
  if (isLinkInline(a) && isLinkInline(b)) {
    return (
      a.data.href === b.data.href &&
      a.children.length === b.children.length &&
      a.children.every((child, i) => {
        const other = b.children[i];
        return other !== undefined && isFlexibleInlineNodeEqual(child, other);
      })
    );
  }
  return false;
}

function isFlexibleBlockNodeEqual(a: FlexibleBlockNode, b: FlexibleBlockNode): boolean {
  if (a.data.type !== b.data.type) return false;
  if (isParagraphBlock(a) && isParagraphBlock(b)) {
    return (
      (a.data.format || '') === (b.data.format || '') &&
      a.children.length === b.children.length &&
      a.children.every((child, i) => {
        const other = b.children[i];
        return other !== undefined && isFlexibleInlineNodeEqual(child, other);
      })
    );
  }
  if (isListBlock(a) && isListBlock(b)) {
    return (
      a.data.listType === b.data.listType &&
      a.children.length === b.children.length &&
      a.children.every((item, i) => {
        const otherItem = b.children[i];
        return (
          otherItem !== undefined &&
          item.children.length === otherItem.children.length &&
          item.children.every((child, j) => {
            const other = otherItem.children[j];
            return other !== undefined && isFlexibleInlineNodeEqual(child, other);
          })
        );
      })
    );
  }
  return false;
}

export function isFlexibleTextEqual(
  a: FlexibleTextContent | null | undefined,
  b: FlexibleTextContent | null | undefined,
): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;
  return a.every((block, i) => {
    const other = b[i];
    return other !== undefined && isFlexibleBlockNodeEqual(block, other);
  });
}

function hydrateFlexibleInlineNodes(
  nodes: FlexibleInlineNode[],
  variables: Partial<Record<string, string>>,
  showVariableNodeWhenValueMissing: boolean,
): FlexibleInlineNode[] {
  return nodes.map((node): FlexibleInlineNode => {
    if (node.data.type === 'variable') {
      const value = variables[node.data.columnId];
      if (value || !showVariableNodeWhenValueMissing) {
        const result: FlexibleInlineTextNode = {
          data: {
            type: 'text',
            text: value ?? '',
            bold: node.data.bold,
            italic: node.data.italic,
            strikethrough: node.data.strikethrough,
            underline: node.data.underline,
            color: node.data.color,
            highlight: node.data.highlight,
          },
        };
        return result;
      }
      return node;
    }
    return node;
  });
}

export function hydrateFlexibleTextVariables(
  content: FlexibleTextContent,
  variables: Partial<Record<string, string>>,
  showVariableNodeWhenValueMissing: boolean = false,
): FlexibleTextContent {
  return content.map((block): FlexibleBlockNode => {
    if (isParagraphBlock(block)) {
      const result: FlexibleParagraphNode = {
        data: block.data,
        children: hydrateFlexibleInlineNodes(block.children, variables, showVariableNodeWhenValueMissing),
      };
      return result;
    }
    if (isListBlock(block)) {
      const result: FlexibleListNode = {
        data: block.data,
        children: block.children.map((item): FlexibleListItemNode => ({
          data: item.data,
          children: hydrateFlexibleInlineNodes(item.children, variables, showVariableNodeWhenValueMissing),
        })),
      };
      return result;
    }
    return block;
  });
}

export function renderFlexibleTextToHtml(
  content: FlexibleTextContent,
  theme?: ThemeProperties,
  options?: RenderOptions,
): string {
  const editorState = flexibleTextToEditorState(content);
  return renderSerializedEditorStateToHtml(editorState, theme, options);
}
